import { useCallback } from "react";
import { useToken } from "../../../../../server/auth/use-token";
import { authHeader } from "../../../../../utils/auth-header";
import { withContentTracking } from "../../../../content-generation/track-content-generation";
import { selfDriving } from "../../../../self-driving/self-driving";

import type { ChannelName } from "../../../../channels/types";
import type { CoPilotResponse } from "../../../../self-driving/types";
import type { CoPilotSchema } from "./forms/schema";

interface RefineQuery {
  prompt: string;
  context: CoPilotResponse["prompt"];
}

export function useCoPilotQueries(
  channel: Exclude<NonNullable<ChannelName>, "whatsapp">,
  mergeTags: string[],
): {
  query: (values: CoPilotSchema) => Promise<CoPilotResponse>;
  refineQuery: (query: RefineQuery) => Promise<CoPilotResponse>;
  refineSection: (
    selection: { section: string; full: string },
    prompt: string,
  ) => Promise<CoPilotResponse>;
} {
  const getToken = useToken();

  const query = useCallback(
    async (values: CoPilotSchema) => {
      const token = await getToken();
      const { data } = await withContentTracking(
        token,
        { contentType: "moment", subType: "copilot", prompt: values.prompt },
        async () => {
          const { data: result, error } = await selfDriving.POST("/moment", {
            body: { sendMethod: channel, mergeTags, ...values },
            ...authHeader(token),
            params: { query: { model: "alpha" } },
          });
          if (error || !result) throw new Error(error.error || "Request failed");
          return result;
        },
      );
      return data;
    },
    [channel, mergeTags, getToken],
  );

  const refineQuery = useCallback(
    async ({ prompt, context }: RefineQuery) => {
      const token = await getToken();
      const { data } = await withContentTracking(
        token,
        { contentType: "moment", subType: "copilot_refine", prompt },
        async () => {
          const { data: result, error } = await selfDriving.POST("/moment/refine", {
            body: { prompt, context },
            ...authHeader(token),
            params: { query: { model: "alpha" } },
          });
          if (error || !result) throw new Error(error.error || "Request failed");
          return result;
        },
      );
      return data;
    },
    [getToken],
  );

  const refineSection = useCallback(
    async (selection: { section: string; full: string }, prompt: string) => {
      const token = await getToken();
      const { data } = await withContentTracking(
        token,
        { contentType: "moment", subType: "copilot_refine_section", prompt },
        async () => {
          const { data: result, error } = await selfDriving.POST("/moment/refine/section", {
            body: { prompt, sendMethod: channel, mergeTags, ...selection },
            ...authHeader(token),
            params: { query: { model: "alpha" } },
          });
          if (error || !result) throw new Error(error.error || "Request failed");
          return result;
        },
      );
      return data;
    },
    [channel, mergeTags, getToken],
  );

  return { query, refineQuery, refineSection };
}
